"use client"

import { useEffect, useMemo, useState } from "react"
import {
  ChevronLeft,
  Clock,
  Users,
  CheckCircle2,
  Wrench,
  GraduationCap,
  Cake,
  ArrowRight,
  Lock,
  Film,
  X,
  Puzzle,
  ChevronDown,
  Search,
} from "lucide-react"
import "./css/LessonOverview.css"

// ---------- Helpers ----------
const levelFromSelectedGrade = (g) => {
  if (g === 5 || g === 6) return "po_groep5_6"
  if (g === 7 || g === 8) return "po_groep7_8"
  if (g === 1 || g === 2) return "vo_onderbouw"
  if (g === 3 || g === 4) return "vo_middenbouw"
  return "po_groep5_6"
}

const formatMinutesRange = (min, max) => {
  if (typeof min !== "number" && typeof max !== "number") return ""
  if (typeof min === "number" && typeof max === "number") {
    if (min === max) return `${min} min`
    return `${min}–${max} min`
  }
  return `${min ?? max} min`
}

const safeArray = (v) => (Array.isArray(v) ? v : [])

// Recommended JSON:
// gameConfig.intro.media = { type:"image"|"video", src:"...", poster:"...", alt:"..." }
const getMedia = (gameConfig) => {
  const intro = gameConfig?.intro || {}
  if (intro?.media?.src) return intro.media

  const img =
    intro?.image ||
    gameConfig?.heroImage ||
    gameConfig?.image ||
    gameConfig?.imageUrl ||
    gameConfig?.thumbnail ||
    gameConfig?.thumbnailUrl ||
    gameConfig?.iconImage ||
    null

  if (img) return { type: "image", src: img, alt: gameConfig?.title || "Les" }
  return null
}

const isSectionLocked = (sec, index, indexData) => {
  if (sec?.locked === true) return true
  if (sec?.requiresPassword === true) return true
  if (sec?.teacherMeta?.locked === true) return true
  if (sec?.teacherMeta?.requiresPassword === true) return true
  if (sec?.access?.locked === true) return true

  const lockedSections = indexData?.lockedSections
  if (Array.isArray(lockedSections)) {
    if (lockedSections.includes(index)) return true
    const id = sec?.id || sec?.key || sec?.slug
    if (id && lockedSections.includes(id)) return true
  }
  return false
}

// --- Video helpers (YouTube vs hosted) ---
const isYouTubeUrl = (url) => {
  if (!url) return false
  try {
    const u = new URL(url)
    return (
      u.hostname.includes("youtube.com") ||
      u.hostname.includes("youtu.be") ||
      u.hostname.includes("youtube-nocookie.com")
    )
  } catch {
    return false
  }
}

const toYouTubeEmbedUrl = (url) => {
  try {
    const u = new URL(url)

    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace("/", "").trim()
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : url
    }

    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v")
      if (id) return `https://www.youtube-nocookie.com/embed/${id}`
      if (u.pathname.startsWith("/embed/")) return url.replace("www.youtube.com", "www.youtube-nocookie.com")
    }

    return url
  } catch {
    return url
  }
}

const getYouTubeId = (url) => {
  try {
    const u = new URL(url)
    if (u.hostname.includes("youtu.be")) return u.pathname.replace("/", "").trim()
    if (u.hostname.includes("youtube.com")) return u.searchParams.get("v") || ""
    return ""
  } catch {
    return ""
  }
}

const getVideoThumbnail = (video) => {
  if (video?.thumbnail) return video.thumbnail
  if (isYouTubeUrl(video?.url)) {
    const id = getYouTubeId(video.url)
    if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`
  }
  if (video?.poster) return video.poster
  return ""
}

// Program-level videos live on index.json:
// gameConfig.practice.videos (recommended)
// also supports: gameConfig.support.videos, gameConfig.teacherOverview.practice.videos (fallbacks)
const getProgramVideos = (gameConfig) => {
  const practice = gameConfig?.practice || {}
  const support = gameConfig?.support || {}
  const teacherPractice = gameConfig?.teacherOverview?.practice || {}

  const raw = practice?.videos || support?.videos || teacherPractice?.videos || []

  return safeArray(raw)
    .map((v) => {
      if (!v) return null
      if (typeof v === "string") {
        return { title: "Video", url: v, duration: "", poster: "", thumbnail: "" }
      }
      return {
        title: v.title || "Video",
        url: v.url || v.href || "",
        duration: v.duration || "",
        poster: v.poster || "",
        thumbnail: v.thumbnail || "",
      }
    })
    .filter((v) => v?.url)
}

// ---------- Component ----------
export default function LessonOverview({
  languageCode,
  beta,
  gameId,
  onBack,
  onStart,
  onStartAtSection, // optional
  isLessonLocked = false,
}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [indexData, setIndexData] = useState(null)
  const [sectionsData, setSectionsData] = useState([])

  // Filters
  const [selectedGrade, setSelectedGrade] = useState(6)
  const [slotMinutes, setSlotMinutes] = useState(45)
  const [audienceMode, setAudienceMode] = useState("year") // "year" | "age"

  // subtle pulse when timing changes
  const [pulseTimes, setPulseTimes] = useState(false)

  // video modal
  const [videoModal, setVideoModal] = useState({ open: false, video: null })

  const audienceOptions = [
    { grade: 5, yearLabel: "Groep 5", ageLabel: "±8–9 jaar" },
    { grade: 6, yearLabel: "Groep 6", ageLabel: "±9–10 jaar" },
    { grade: 7, yearLabel: "Groep 7", ageLabel: "±10–11 jaar" },
    { grade: 8, yearLabel: "Groep 8", ageLabel: "±11–12 jaar" },
    { grade: 1, yearLabel: "Klas 1 VO", ageLabel: "±12–13 jaar" },
    { grade: 2, yearLabel: "Klas 2 VO", ageLabel: "±13–14 jaar" },
    { grade: 3, yearLabel: "Klas 3 VO", ageLabel: "±14–15 jaar" },
    { grade: 4, yearLabel: "Klas 4 VO", ageLabel: "±15–16 jaar" },
  ]

  const contentPath = useMemo(() => {
    const base = beta ? "/content-beta" : "/content"
    return `${base}/${languageCode || "nl"}`
  }, [beta, languageCode])

  useEffect(() => {
    let alive = true

    const load = async () => {
      setLoading(true)
      setError(null)

      try {
        const idxRes = await fetch(`${contentPath}/${gameId}/index.json`, { cache: "no-store" })
        if (!idxRes.ok) throw new Error("Kon les-overzicht niet laden (index.json).")
        const idx = await idxRes.json()

        const sectionFiles = idx?.sections || []
        const sectionResults = await Promise.all(
          sectionFiles.map(async (file) => {
            const r = await fetch(`${contentPath}/${gameId}/${file}`, { cache: "no-store" })
            if (!r.ok) return null
            return r.json()
          }),
        )

        if (!alive) return
        setIndexData(idx)
        setSectionsData(sectionResults.filter(Boolean))
      } catch (e) {
        if (!alive) return
        setError(e.message || "Er ging iets mis.")
      } finally {
        if (!alive) return
        setLoading(false)
      }
    }

    load()
    return () => {
      alive = false
    }
  }, [contentPath, gameId])

  // Sluit modal met ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setVideoModal({ open: false, video: null })
    }
    if (videoModal.open) window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [videoModal.open])

  const gameConfig = indexData?.gameConfig || {}
  const teacherOverview = gameConfig?.teacherOverview || null
  const intro = gameConfig?.intro || {}
  const media = getMedia(gameConfig)

  const longDescription = intro?.longDescription || ""
  const outcomes = safeArray(intro?.outcomes)
  const materials = safeArray(intro?.materials)
  const workForms = safeArray(intro?.workForms)
  const sloGoals = safeArray(intro?.sloGoals)

  // Slots
  const slotOptions =
    teacherOverview?.lessonSlots || [
      { slotMinutes: 30, label: "30 min" },
      { slotMinutes: 45, label: "45 min" },
      { slotMinutes: 60, label: "60 min" },
    ]

  const selectedLevel = levelFromSelectedGrade(selectedGrade)

  // Total time
  const totalByLevel = teacherOverview?.totalTime?.byLevel || {}
  const totalForLevel = totalByLevel?.[selectedLevel] || null
  const totalLabel = totalForLevel ? formatMinutesRange(totalForLevel.min, totalForLevel.max) : ""

  const estimatedSlots = useMemo(() => {
    if (!slotMinutes) return null
    const minutes = totalForLevel?.max ?? totalForLevel?.min
    if (typeof minutes !== "number") return null
    return Math.max(1, Math.ceil(minutes / slotMinutes))
  }, [totalForLevel, slotMinutes])

  // pulse when timing changes
  useEffect(() => {
    setPulseTimes(true)
    const t = setTimeout(() => setPulseTimes(false), 320)
    return () => clearTimeout(t)
  }, [selectedGrade, slotMinutes])

  // Program-level games
  const memoryUrl = useMemo(() => `/games/memory/?courseId=${encodeURIComponent(gameId)}`, [gameId])
  const wordsearchUrl = useMemo(() => `/games/woordzoeker/?courseId=${encodeURIComponent(gameId)}`, [gameId])

  // Program-level shorts
  const programVideos = useMemo(() => getProgramVideos(gameConfig), [gameConfig])

  // Timeline items
  const timelineItems = sectionsData
    .filter((sec) => !sec?.teacherMeta?.hideFromOverview)
    .map((sec, i) => {
      const secTimeByLevel = sec?.teacherMeta?.time?.byLevel || {}
      const secTime = secTimeByLevel?.[selectedLevel] || null
      const timeLabel = secTime ? formatMinutesRange(secTime.min, secTime.max) : ""

      const title = sec?.title || sec?.mainContent || `Onderdeel ${i + 1}`
      const goalShort = sec?.teacherMeta?.goalShort || ""
      const activityType = sec?.teacherMeta?.activityType || ""
      const workFormLocal = Array.isArray(sec?.teacherMeta?.workForm) ? sec.teacherMeta.workForm : []

      const originalIndex = sectionsData.indexOf(sec)
      return {
        index: originalIndex,
        title,
        goalShort,
        activityType,
        workForm: workFormLocal,
        timeLabel,
        locked: isLessonLocked || isSectionLocked(sec, i, indexData),
      }
    })

  const startFromSection = (sectionIndex) => {
    if (typeof onStartAtSection === "function") onStartAtSection(sectionIndex)
    else onStart?.()
  }

  const teacherTips = safeArray(teacherOverview?.planningNotes?.teacherNotes)

  if (loading) {
    return (
      <div className="lesson-overview-page">
        <div className="lesson-overview-body">
          <div className="lesson-overview-loading">Overzicht laden…</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="lesson-overview-page">
        <div className="lesson-overview-body">
          <div className="lesson-overview-error">{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="lesson-overview-page lesson-overview-v2">
      {/* --- Video modal --- */}
      {videoModal.open && videoModal.video && (
        <div
          className="tt-modal-overlay"
          onMouseDown={() => setVideoModal({ open: false, video: null })}
          role="dialog"
          aria-modal="true"
        >
          <div className="tt-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="tt-modal-header">
              <div className="tt-modal-title">
                <Film size={16} />
                <span>{videoModal.video.title || "Video"}</span>
                {videoModal.video.duration ? (
                  <span className="tt-modal-sub">· {videoModal.video.duration}</span>
                ) : null}
              </div>
              <button
                type="button"
                className="tt-modal-close"
                onClick={() => setVideoModal({ open: false, video: null })}
                aria-label="Sluiten"
              >
                <X size={16} />
              </button>
            </div>

            <div className="tt-modal-body">
              {isYouTubeUrl(videoModal.video.url) ? (
                <div className="tt-video-embed">
                  <iframe
                    src={`${toYouTubeEmbedUrl(videoModal.video.url)}?autoplay=1&rel=0`}
                    title={videoModal.video.title || "Video"}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <video
                  className="tt-video-player"
                  controls
                  autoPlay
                  poster={videoModal.video.poster || ""}
                  src={videoModal.video.url}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- HERO --- */}
      <header className="lesson-intro">
        <div className="lesson-intro-topbar">
          <button className="lesson-overview-back" onClick={onBack}>
            <ChevronLeft size={18} /> Terug
          </button>
        </div>

        <div className="lesson-intro-grid">
          {/* Links */}
          <div className="lesson-intro-text">
            <div className="lesson-topintro">
              <h1 className="lesson-intro-title">{gameConfig?.title || "Les"}</h1>
              <p className="lesson-intro-subtitle">{gameConfig?.description || "Bekijk de opbouw en planning."}</p>

              {longDescription && <p className="lesson-intro-long">{longDescription}</p>}

              <div className="lesson-intro-cta">
                <button className="start-lesson-btn start-lesson-btn--hero" onClick={onStart}>
                  Start les {isLessonLocked ? <Lock className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {outcomes.length > 0 && (
              <div className="lesson-outcomes">
                <h3>
                  <CheckCircle2 size={18} /> Dit ga je leren
                </h3>
                <ul>
                  {outcomes.slice(0, 5).map((o, i) => (
                    <li key={i}>{o}</li>
                  ))}
                </ul>

                {sloGoals.length > 0 && (
                  <div className="lesson-slo">
                    <div className="lesson-slo-pills">
                      {sloGoals.slice(0, 8).map((g) => {
                        const label = typeof g === "string" ? g : g?.label
                        const key = typeof g === "string" ? g : g?.key || g?.label
                        if (!label) return null
                        return (
                          <span key={key} className="hero-chip hero-chip--slo">
                            {label}
                          </span>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(materials.length > 0 || workForms.length > 0) && (
              <div className="lesson-kitchen-sink">
                {workForms.length > 0 && (
                  <div className="info-line">
                    <Users size={16} />
                    <div>
                      <strong>Werkvorm</strong>
                      <div className="muted">{workForms.slice(0, 4).join(" · ")}</div>
                    </div>
                  </div>
                )}
                {materials.length > 0 && (
                  <div className="info-line">
                    <Wrench size={16} />
                    <div>
                      <strong>Benodigd</strong>
                      <div className="muted">{materials.slice(0, 4).join(" · ")}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Rechts */}
          <div className="lesson-intro-media">
            {media?.type === "video" ? (
              <video controls poster={media.poster || ""} className="lesson-media-box" src={media.src} />
            ) : media?.type === "image" ? (
              <img className="lesson-media-box" src={media.src} alt={media.alt || ""} />
            ) : (
              <div className="lesson-media-placeholder">Geen afbeelding gevonden</div>
            )}
          </div>
        </div>
      </header>

      {/* --- BODY --- */}
      <div className="lesson-overview-body">
        {/* ===================== Lesopbouw (full-width blok: header + lesplanning + tijdlijn) ===================== */}
        <section className="lesson-plan-block" aria-label="Lesopbouw">
          {/* Header (titel + startknop) */}
          <div className="lesson-plan-header">
            <div className="lesson-plan-header-left">
              <h2 className="lesson-plan-title">Lesopbouw</h2>
<p className="lesson-plan-sub">Kies je groep en lesduur. De tijden in de tijdlijn passen zich aan.</p>
            </div>

            <div className="lesson-plan-header-right">
              <button className="start-lesson-btn" onClick={onStart}>
                Start les {isLessonLocked ? <Lock className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Lesplanning bar (filters + resultaten) */}
          <div className={`timeline-controls--sticky ${pulseTimes ? "pulse-times" : ""}`} id="lesplanning">
           <div className="planning-bar">
  <div className="planning-row">
    {/* Left: Groep segmented */}
    <div className="planning-filters">
      <div className="filter filter--grade">
        <span className="filter-label">Groep</span>

        <div className="segmented segmented--grade" role="tablist" aria-label="Kies groep">
          {[5, 6, 7, 8].map((g) => (
            <button
              key={g}
              type="button"
              className={`segmented-btn ${selectedGrade === g ? "active" : ""}`}
              onClick={() => setSelectedGrade(g)}
              role="tab"
              aria-selected={selectedGrade === g}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      {/* Right: Lesduur dropdown */}
      <div className="filter filter--duration">
        <span className="filter-label">Lesduur</span>

        <div className="select-wrap">
          <Clock size={16} className="select-icon-left" />
          <select
            className="filter-select filter-select--with-icons"
            value={slotMinutes}
            onChange={(e) => setSlotMinutes(Number(e.target.value))}
            aria-label="Kies lesduur"
          >
            {slotOptions.map((s) => (
              <option key={s.slotMinutes} value={s.slotMinutes}>
                {s.label}
              </option>
            ))}
          </select>
          <ChevronDown size={16} className="select-icon-right" />
        </div>
      </div>
    </div>

    {/* Results */}
    <div className="planning-results">
      <div className="planning-result">
        <Clock size={16} />
        <span>Totale tijd</span>
        <strong className={pulseTimes ? "time-pulse" : ""}>{totalLabel || "—"}</strong>
      </div>

      <div className="planning-result subtle">
        <span>Aantal lessen</span>
        <strong>{estimatedSlots ? `${estimatedSlots}` : "—"}</strong>
      </div>
    </div>
  </div>
</div>

          </div>

          {/* Tijdlijn direct onder lesplanning */}
          <div className="timeline">
            {timelineItems.map((item) => (
              <button
                key={item.index}
                className={`timeline-item timeline-item-click ${item.locked ? "timeline-item--locked" : ""}`}
                onClick={() => {
                  if (item.locked) return
                  startFromSection(item.index)
                }}
                disabled={item.locked}
                aria-disabled={item.locked}
                type="button"
              >
                <div className="timeline-dot" />
                <div className="timeline-card">
                  <div className="timeline-top">
                    <h3 className="timeline-title">
                      {item.title}
                      {item.locked && (
                        <span className="timeline-lock" title="Vergrendeld" aria-label="Vergrendeld">
                          <Lock size={14} />
                        </span>
                      )}
                    </h3>
                    <div className={`timeline-time ${pulseTimes ? "time-pulse" : ""}`}>{item.timeLabel || "—"}</div>
                  </div>

                  {(item.activityType || item.workForm?.length > 0) && (
                    <div className="timeline-meta">
                      {item.activityType && <span className="meta-chip">{item.activityType}</span>}
                      {item.workForm?.slice(0, 3).map((wf) => (
                        <span key={wf} className="meta-chip ghost">
                          {wf}
                        </span>
                      ))}
                    </div>
                  )}

                  {item.goalShort && <p className="timeline-goal">{item.goalShort}</p>}
                </div>
              </button>
            ))}
          </div>

          {teacherTips.length > 0 && (
            <div className="lesson-overview-notes">
              <h4>Tip voor de leerkracht</h4>
              <ul>
                {teacherTips.slice(0, 2).map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* ===================== Exit-banner: games + korte tips ===================== */}
        <section className="exit-banner" aria-label="Oefenen en korte tips">
          <div className="exit-banner-inner">
            <div className="exit-banner-header">
              <div>
               <h2 className="exit-banner-title">Speel & ontdek</h2>
<p className="exit-banner-sub">
  Klaar? Speel een kort spel en ontdek nieuwe woorden en ideeën.
</p>
              </div>
            </div>

            <div className="kids-games-grid">
  <a className="kids-game-card" href={memoryUrl} target="_blank" rel="noreferrer">
    <div className="kids-game-icon">
      <Puzzle size={22} />
    </div>

    <div className="kids-game-content">
      <h4>Memory Match</h4>
      <p>Match begrippen en train je brein</p>
    </div>

    <div className="kids-game-meta">
      <span>🕒 5–10 min</span>
      <span>⭐ Makkelijk → Moeilijk</span>
    </div>
  </a>

  <a className="kids-game-card" href={wordsearchUrl} target="_blank" rel="noreferrer">
    <div className="kids-game-icon">
      <Search size={22} />
    </div>

    <div className="kids-game-content">
      <h4>Woordzoeker</h4>
      <p>Zoek woorden in het rooster</p>
    </div>

    <div className="kids-game-meta">
      <span>🕒 5–10 min</span>
      <span>⭐ Makkelijk → Moeilijk</span>
    </div>
  </a>
</div>


            {programVideos.length > 0 && (
              <div className="practice-videos">
                <div className="practice-videos-title">
                  <Film size={16} /> Korte tips
                </div>

                <div className="practice-video-grid">
                  {programVideos.slice(0, 8).map((v, idx) => {
                    const thumb = getVideoThumbnail(v)
                    return (
                      <button
                        key={`${v.url}-${idx}`}
                        type="button"
                        className="practice-video-card"
                        onClick={() => setVideoModal({ open: true, video: v })}
                        title={v.title}
                      >
                        <div className="practice-video-thumb">
                          {thumb ? (
                            <img src={thumb} alt={v.title || "Video"} loading="lazy" />
                          ) : (
                            <div className="practice-video-thumb--empty">
                              <Film size={18} />
                            </div>
                          )}

                          {v.duration ? <div className="practice-video-badge">{v.duration}</div> : null}
                        </div>

                        <div className="practice-video-info">
                          <div className="practice-video-title">{v.title}</div>
                          <div className="practice-video-sub">Klik om te bekijken</div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}
